import React, { useEffect, useRef, useState } from "react";
import { ArrowRightOnRectangleIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { useLocation } from "react-router-dom";
import { useAccount, useMsal } from "@azure/msal-react";
import { findNavigationContext } from "../navigation/navigationConfig";
import { signOutUtilities } from "../auth/localSession";

function TopHeader() {
  const { pathname } = useLocation();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const context = findNavigationContext(pathname);
  const environmentTextClassName = context?.environment.key === "development" ? "text-[#009b48]" : "text-[#e12b38]";
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <header className="flex h-11 shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4">
      <div className="flex min-w-0 items-center gap-2 text-sm">
        {context && (
          <>
            <span className="truncate font-normal text-slate-800">{context.product.name}</span>
            <span className="text-slate-300">/</span>
            <span className={`shrink-0 text-sm font-bold ${environmentTextClassName}`}>{context.environment.label}</span>
            <span className="text-slate-300">/</span>
            <span className="truncate font-semibold text-slate-900">{context.feature.label}</span>
          </>
        )}
        {!context && pathname === "/" && (
          <span className="font-medium text-slate-900">Overview</span>
        )}
      </div>

      <div ref={accountMenuRef} className="relative flex shrink-0 items-center text-xs text-slate-500">
        <button
          type="button"
          aria-expanded={isAccountMenuOpen}
          aria-haspopup="menu"
          onClick={() => setIsAccountMenuOpen((current) => !current)}
          className="flex min-w-0 items-center gap-2 rounded-sm px-1.5 py-1 text-left transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        >
          <UserCircleIcon className="h-5 w-5 shrink-0 text-slate-400" />
          <span className="hidden max-w-[12rem] truncate sm:inline">{account?.name || "Signed-in user"}</span>
        </button>
        {isAccountMenuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full z-30 mt-1 w-32 overflow-hidden rounded-sm border border-slate-200 bg-white p-0.5 shadow-[0_1px_2px_rgba(15,23,42,0.12),0_3px_8px_rgba(15,23,42,0.14)]"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setIsAccountMenuOpen(false);
                signOutUtilities();
              }}
              className="flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400"
            >
              <ArrowRightOnRectangleIcon className="h-3.5 w-3.5 text-slate-500" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export default TopHeader;
